import clsx from 'clsx';
import React, { useState } from 'react';
import { BiImageAlt } from 'react-icons/bi';

import { imageVariantLink } from '@/common/media';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { ButtonSmall } from '@/components/Button';
import { ChosenYachtControls } from '@/components/ChosenYachtControls';
import { usePhotoUpload } from '@/components/Inputs/usePhotoUpload';
import OutOfWaterBadge from '@/components/OutOfWaterBadge/OutOfWaterBadge';
import { Skeleton } from '@/components/Skeleton';
import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { useEvents } from '@/store/events/events.store';
import { useVessels } from '@/store/vessels/vessels.store';

import styles from './ChosenYachtWidget.module.scss';

interface ChosenYachtWidgetProps {
  yachtImage?: string;
  onMarinaClick?: () => void;
  onServiceClick?: () => void;
}

export const ChosenYachtWidget: React.FC<ChosenYachtWidgetProps> = ({
  yachtImage,
  onMarinaClick,
  onServiceClick,
}) => {
  const { vessel, myVessels, setVesselImage } = useVessels();
  const { outOfWaterStatuses, outOfWaterEnds } = useEvents();
  const { can } = usePermissions();
  const [loadedSrc, setLoadedSrc] = useState<string>();

  const isFirstTimeUser = !myVessels || myVessels.length === 0;
  const isOutOfWater = vessel ? (outOfWaterStatuses[vessel.id] ?? false) : false;
  // ponytail: no placeholder art on desktop — an empty framed slot with an upload
  // button reads cleaner than the fallback icon. Desktop never collapses the slot.
  const photo = usePhotoUpload({
    shape: 'rect',
    onPhoto: (file) => setVesselImage(file, vessel?.id),
  });
  const imageSrc = photo.preview ?? (imageVariantLink(vessel?.image, 900, 414) || yachtImage);
  const canUploadImage = Boolean(vessel) && can(PERMISSIONS.vessel.EditVesselInfo);
  // Shares AuthorizedImage's cached request; only read so a failed fetch ends the skeleton.
  const { error: imageFetchFailed } = useAuthorizedFileUrl(imageSrc);
  // Vessels still loading, or the photo still resolving and decoding.
  const imagePending =
    myVessels === undefined || (!!imageSrc && !imageFetchFailed && loadedSrc !== imageSrc);

  return (
    <div className={styles.chosenYachtWidget}>
      {/*<YachtPicker className={styles.yachtName} />*/}
      <div
        className={clsx(
          styles.chosenYachtImageWrapper,
          !imageSrc && !imagePending && styles.chosenYachtImageEmpty,
        )}
        {...(canUploadImage && photo.dropProps)}
      >
        {imagePending && <Skeleton variant="block" className={styles.imageSkeleton} />}
        {imageSrc ? (
          <AuthorizedImage
            className={styles.chosenYachtImage}
            src={imageSrc}
            alt=""
            draggable={false}
            onLoad={() => setLoadedSrc(imageSrc)}
            onError={() => setLoadedSrc(imageSrc)}
          />
        ) : (
          !imagePending &&
          canUploadImage && (
            <ButtonSmall type="button" className={styles.uploadImageButton} onClick={photo.pick}>
              <BiImageAlt />
              <span>Upload Vessel Image</span>
            </ButtonSmall>
          )
        )}
        {photo.overlay}
        {isOutOfWater && (
          <div className={styles.outOfWaterBadge}>
            <OutOfWaterBadge
              size="small"
              variant="warning"
              align="left"
              until={vessel ? outOfWaterEnds[vessel.id] : undefined}
            />
          </div>
        )}
      </div>
      {!isFirstTimeUser && (
        <ChosenYachtControls
          onMarinaClick={onMarinaClick}
          onServiceClick={onServiceClick}
          className={styles.chosenYachtButtons}
        />
      )}
      {photo.element}
    </div>
  );
};
